import React, { Fragment, useState, useEffect } from 'react';
import { getInconsistencyReportService } from '../../../services/InconsistencyReportService';
import { getBRCorelationService } from '../../../services/MasterService';
import { Table, Button, DatePicker, message, Select, Tag } from 'antd';
import { Col, Row } from 'react-bootstrap';
import Card from '../../../components/bootstrap/card';
import { getAllAdminIECsService, getReportSummaryData } from '../../../services/FileDataService';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import ReportDateFilter from '../../../components/ReportDateFilter';
import { getTodaysDate } from '../../../utilities/utils';
import { Radio } from 'antd';
import useColumnVisibility from '../../../hooks/useColumnVisibility';
import ColumnSelector from '../../../components/common/ColumnSelector';

const InconsistencyReport = ({ iecNo: propIec, dateRange: propDates }) => {
    const tableStyles = `
        .inconsistency-table .ant-table-thead > tr > th,
        .inconsistency-summary-table .ant-table-thead > tr > th {
            white-space: normal !important;
            word-break: break-word !important;
            overflow: visible !important;
            text-overflow: unset !important;
            vertical-align: middle !important;
            line-height: 1.3 !important;
            padding-top: 8px !important;
            padding-bottom: 8px !important;
        }
        .inconsistency-table .ant-table-tbody > tr > td,
        .inconsistency-summary-table .ant-table-tbody > tr > td {
            white-space: nowrap;
        }
        .inconsistency-table .ant-table-container,
        .inconsistency-summary-table .ant-table-container {
            overflow-x: auto !important;
        }
        .inconsistency-table .ant-table-header,
        .inconsistency-summary-table .ant-table-header {
            overflow: hidden !important;
        }
        .inconsistency-table .ant-table-sticky-holder,
        .inconsistency-summary-table .ant-table-sticky-holder {
            overflow: hidden !important;
        }
    `;
    const location = useLocation();
    const [jsonData, setJsonData] = useState([]);
    const [summaryData, setSummaryData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSummaryLoading, setIsSummaryLoading] = useState(false);
    const [branchCodeNames, setBranchCodeNames] = useState(null);
    const [IECOptions, setIECOptions] = useState([]);
    const userRole = localStorage.getItem('role');
    const [selectedIEC, setSelectedIEC] = useState(propIec || localStorage.getItem('iec_no'));
    const [selectedIECName, setSelectedIECName] = useState(localStorage.getItem('name'));
    const [isValid, setIsValid] = useState(true);
    const [selectedBranchCodes, setSelectedBranchCodes] = useState([]);
    const [reportMode, setReportMode] = useState('data');

    const currentYear = dayjs().year();
    const defaultFrom = dayjs(`${currentYear}-01-01`);
    const defaultTo = dayjs(`${currentYear}-12-31`);
    const [dates, setDates] = useState(() => {
        if (propDates && propDates.length === 2) {
            return [dayjs(propDates[0]), dayjs(propDates[1])];
        }
        if (location.state?.passedDates?.length === 2) {
            return [
                dayjs(location.state.passedDates[0]),
                dayjs(location.state.passedDates[1]),
            ];
        }
        return [defaultFrom, defaultTo];
    });

    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const formattedDate = `${day}-${month}-${year}`;

    // ─── Summary table columns ───────────────────────────────────────────────
    const pctRender = (val) => {
        const num = parseFloat(val) || 0;
        const color = num >= 90 ? '#52c41a' : num >= 70 ? '#faad14' : '#ff4d4f';
        return <span style={{ color, fontWeight: 600 }}>{num}%</span>;
    };

    const summaryColumns = [
        {
            title: 'Business Unit',
            dataIndex: 'business_unit',
            key: 'business_unit',
            fixed: 'left',
            width: 130,
            render: (v) => <strong>{v}</strong>,
        },
        { title: 'Total', dataIndex: 'total', key: 'total', align: 'right', width: 70 },
        // HSN Consistency
        {
            title: 'HSN Inconsistency Report',
            children: [
                // { title: 'Consistent', dataIndex: reportMode === 'master' ? 'hsn_consistent_wrt_master' : 'hsn_consistent', key: 'hsn_consistent', align: 'right', width: 90 },
                // { title: 'Inconsistent', dataIndex: reportMode === 'master' ? 'hsn_inconsistent_wrt_master' : 'hsn_inconsistent', key: 'hsn_inconsistent', align: 'right', width: 100 },
                { title: '% Consistent', dataIndex: reportMode === 'master' ? 'hsn_consistent_pct_wrt_master' : 'hsn_consistent_pct', key: 'hsn_consistent_pct', align: 'right', width: 110, render: pctRender },
                { title: '% Inconsistent', dataIndex: reportMode === 'master' ? 'hsn_inconsistent_pct_wrt_master' : 'hsn_inconsistent_pct', key: 'hsn_inconsistent_pct', align: 'right', width: 120, render: (v) => <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{parseFloat(v) || 0}%</span> },
            ],
        },
        // BCD Consistency
        {
            title: 'Duty Rate Inconsistency',
            children: [
                // { title: 'Consistent', dataIndex: 'bcd_consistent', key: 'bcd_consistent', align: 'right', width: 90 },
                // { title: 'Inconsistent', dataIndex: 'bcd_inconsistent', key: 'bcd_inconsistent', align: 'right', width: 100 },
                { title: '% Consistent', dataIndex: 'bcd_consistent_pct', key: 'bcd_consistent_pct', align: 'right', width: 110, render: pctRender },
                { title: '% Inconsistent', dataIndex: 'bcd_inconsistent_pct', key: 'bcd_inconsistent_pct', align: 'right', width: 120, render: (v) => <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{parseFloat(v) || 0}%</span> },
            ],
        },
        // IGST Consistency
        {
            title: 'IGST Consistency',
            children: [
                // { title: 'Consistent', dataIndex: 'bcd_consistent', key: 'bcd_consistent', align: 'right', width: 90 },
                // { title: 'Inconsistent', dataIndex: 'bcd_inconsistent', key: 'bcd_inconsistent', align: 'right', width: 100 },
                { title: '% Consistent', dataIndex: 'igst_consistent_pct', key: 'igst_consistent_pct', align: 'right', width: 110, render: pctRender },
                { title: '% Inconsistent', dataIndex: 'igst_inconsistent_pct', key: 'igst_inconsistent_pct', align: 'right', width: 120, render: (v) => <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{parseFloat(v) || 0}%</span> },
            ],
        },

        // BCD Accuracy
        {
            title: 'Duty Rate Accuracy',
            children: [
                // { title: 'Accurate', dataIndex: 'bcd_accurate', key: 'bcd_accurate', align: 'right', width: 80 },
                // { title: 'Inaccurate', dataIndex: 'bcd_inaccurate', key: 'bcd_inaccurate', align: 'right', width: 90 },
                { title: '% Accurate', dataIndex: 'bcd_accurate_pct', key: 'bcd_accurate_pct', align: 'right', width: 100, render: pctRender },
                { title: '% Inaccurate', dataIndex: 'bcd_inaccurate_pct', key: 'bcd_inaccurate_pct', align: 'right', width: 105, render: (v) => <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{parseFloat(v) || 0}%</span> },
            ],
        },
        // IGST Accuracy
        {
            title: 'IGST Rate Accuracy',
            children: [
                // { title: 'Accurate', dataIndex: 'bcd_accurate', key: 'bcd_accurate', align: 'right', width: 80 },
                // { title: 'Inaccurate', dataIndex: 'bcd_inaccurate', key: 'bcd_inaccurate', align: 'right', width: 90 },
                { title: '% Accurate', dataIndex: 'igst_accurate_pct', key: 'igst_accurate_pct', align: 'right', width: 100, render: pctRender },
                { title: '% Inaccurate', dataIndex: 'igst_inaccurate_pct', key: 'igst_inaccurate_pct', align: 'right', width: 105, render: (v) => <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{parseFloat(v) || 0}%</span> },
            ],
        },
        // HSN Accuracy
        {
            title: 'GTM HSN Adherence/Non Adherence ',
            children: [
                // { title: 'Accurate', dataIndex: 'hsn_accurate', key: 'hsn_accurate', align: 'right', width: 80 },
                // { title: 'Inaccurate', dataIndex: 'hsn_inaccurate', key: 'hsn_inaccurate', align: 'right', width: 90 },
                { title: '% Accurate', dataIndex: 'hsn_accurate_pct', key: 'hsn_accurate_pct', align: 'right', width: 100, render: pctRender },
                { title: '% Inaccurate', dataIndex: 'hsn_inaccurate_pct', key: 'hsn_inaccurate_pct', align: 'right', width: 105, render: (v) => <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{parseFloat(v) || 0}%</span> },
            ],
        },
    ].filter(col => {
        if (reportMode === 'data') {
            return !['HSN Consistency (vs Master)', 'Duty Rate Consistency (vs Master)', 'IGST Rate Consistency (vs Master)'].includes(col.title);
        } else {
            return true;
        }
    });

    // ─── Detail table columns ─────────────────────────────────────────────────
    const columnNames = [
        { title: 'BU', dataIndex: 'business_unit', key: 'business_unit', header: 'BU', width: 160, fixed: 'left' },
        { title: 'PLANT NAME', dataIndex: 'plant_name', key: 'plant_name', header: 'PLANT NAME', width: 150, fixed: 'left' },
        { title: 'ENTITY', dataIndex: 'entity', key: 'entity', header: 'ENTITY', width: 110 },
        { title: 'ORG CODE', dataIndex: 'org_code', key: 'org_code', header: 'ORG CODE', width: 125 },
        { title: 'IEC NO', dataIndex: 'iec_no', key: 'iec_no', header: 'IEC NO', width: 110 },
        { title: 'UID No.', dataIndex: 'reference_code', key: 'reference_code', header: 'UID No.', width: 120 },
        { title: 'BOE NO', dataIndex: 'be_no', key: 'be_no', header: 'BOE NO', width: 110 },
        {
            title: 'BOE DATE', dataIndex: 'be_date', key: 'be_date', header: 'BOE DATE', width: 120,
            render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : '',
        },
        { title: 'HSN Code', dataIndex: 'boe_hsn', key: 'boe_hsn', header: 'HSN Code', width: 120 },
        { title: 'DESCRIPTION', dataIndex: 'description', key: 'description', header: 'DESCRIPTION', width: 150 },
        { title: 'PART NUMBER', dataIndex: 'part_number', key: 'part_number', header: 'PART NUMBER', width: 150 },
        { title: "ITEM ASSESS VALUE", dataIndex: "assess_value", key: "assess_value", header: "ITEM ASSESS VALUE", width: 195 },
        { title: 'BOE BCD', dataIndex: 'boe_bcd', key: 'boe_bcd', header: 'BOE BCD', align: 'right', width: 110 },
        { title: 'BCD Payable Amt.', dataIndex: 'bcd_amount', key: 'bcd_amount', header: 'BCD Payable Amt.', align: 'right', width: 190 },
        { title: "SWS Rate", dataIndex: "sws_rate", key: "sws_rate", header: "SWS Rate", align: "right", width: 120 },
        { title: "SWS Payable Amt.", dataIndex: "sws_amount", key: "sws_amount", header: "SWS Payable Amt.", align: "right", width: 190 },
        { title: "BOE IGST", dataIndex: "boe_igst", key: "boe_igst", header: "BOE IGST", align: "right", width: 120 },
        { title: "IGST Payable Amt.", dataIndex: "igst_amount", key: "igst_amount", header: "IGST Payable Amt.", align: "right", width: 195 },
        { title: 'COO Code', dataIndex: 'boe_country', key: 'boe_country', header: 'COO Code', width: 120 },
        {
            title: 'HSN Consistency', dataIndex: reportMode === 'master' ? 'hsn_inconsistency_wrt_master' : 'hsn_consistency_remark', key: 'hsn_consistency', header: 'HSN Consistency', width: 180,
            render: (v) => v ? <Tag color={v.toLowerCase() === 'consistent' ? 'green' : 'red'}>{v}</Tag> : ''
        },
        {
            title: 'Duty Rate Consistency', dataIndex: 'bcd_inconsistency_wrt_master', key: 'bcd_inconsistency_wrt_master', header: 'Duty Rate Consistency', width: 235,
            render: (v) => v ? <Tag color={v.toLowerCase() === 'consistent' ? 'green' : 'red'}>{v}</Tag> : ''
        },
        {
            title: 'IGST Rate Consistency', dataIndex: 'igst_inconsistency_wrt_master', key: 'igst_inconsistency_wrt_master', header: 'IGST Rate Consistency', width: 235,
            render: (v) => v ? <Tag color={v.toLowerCase() === 'consistent' ? 'green' : 'red'}>{v}</Tag> : ''
        },
        {
            title: 'HSN ADHERENCE', dataIndex: 'hsn_adherence_status', key: 'hsn_adherence_status', header: 'HSN ADHERENCE', width: 160,
            render: (v) => v ? <Tag color={v.toLowerCase() === 'adherence' ? 'blue' : 'orange'}>{v}</Tag> : ''
        },
        { title: 'EXPECTED MASTER BCD', dataIndex: 'expected_master_bcd', key: 'expected_master_bcd', header: 'EXPECTED MASTER BCD', align: 'right', width: 215 },
        { title: 'EXPECTED MASTER IGST', dataIndex: 'expected_master_igst', key: 'expected_master_igst', header: 'EXPECTED MASTER IGST', align: 'right', width: 225 },
        {
            title: 'Duty Rate Accuracy Remark', dataIndex: 'master_bcd_equal', key: 'master_bcd_equal', header: 'Duty Rate Accuracy Remark', width: 280,
            render: (v) => v === true || v === 'true' ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>
        },
        {
            title: 'IGST Rate Accuracy Remark', dataIndex: 'master_igst_equal', key: 'master_igst_equal', header: 'IGST Rate Accuracy Remark', width: 280,
            render: (v) => v === true || v === 'true' ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>
        },
        { title: 'HSN+Part No+Country code BCD', dataIndex: 'comb1_bcd', key: 'comb1_bcd', header: 'HSN+Part No+Country code BCD', align: 'right', width: 300 },
        { title: 'HSN+Part No+Country code IGST', dataIndex: 'comb1_igst', key: 'comb1_igst', header: 'HSN+Part No+Country code IGST', align: 'right', width: 310 },
        { title: 'HSN+Part No BCD', dataIndex: 'comb2_bcd', key: 'comb2_bcd', header: 'HSN+Part No BCD', align: 'right', width: 190 },
        { title: 'HSN+Part No IGST', dataIndex: 'comb2_igst', key: 'comb2_igst', header: 'HSN+Part No IGST', align: 'right', width: 195 },
        { title: 'HSN BCD', dataIndex: 'comb3_bcd', key: 'comb3_bcd', header: 'HSN BCD', align: 'right', width: 110 },
        { title: 'HSN IGST', dataIndex: 'comb3_igst', key: 'comb3_igst', header: 'HSN IGST', align: 'right', width: 120 },
    ];

    const { visibleKeys, setVisibleKeys, filteredColumns, resetToDefault } =
        useColumnVisibility('InconsistencyReport', columnNames);

    // ─── Data fetching ────────────────────────────────────────────────────────
    const buildFilters = () => ({
        from_date: dates?.[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null,
        to_date: dates?.[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null,
        br_code: selectedBranchCodes?.length ? selectedBranchCodes : null,
        bill_no: null,
    });

    const fetchData = async () => {
        const filters = buildFilters();
        try {
            setIsLoading(true);
            const response = await getInconsistencyReportService(selectedIEC, filters);
            setJsonData(response || []);
        } catch (error) {
            console.error('Error fetching inconsistency report data:', error);
            message.error('Failed to fetch report data');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchSummary = async () => {
        const filters = buildFilters();
        try {
            setIsSummaryLoading(true);
            const response = await getReportSummaryData('Inconsistency-Summary', filters, selectedIEC);
            if (response?.data?.data) {
                setSummaryData(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching summary:', err);
        } finally {
            setIsSummaryLoading(false);
        }
    };

    const fetchBranchCodeNames = async () => {
        try {
            const response = await getBRCorelationService(selectedIEC);
            if (response) {
                let i = 0;
                const options = response.map(item => ({
                    value: item.branch_code,
                    label: `${item.branch_code} / ${item.plant_name} (${item.business_unit})`,
                    key: i++,
                }));
                setBranchCodeNames(options);
            }
        } catch (error) {
            console.error('Error fetching branch code names:', error);
        }
    };

    useEffect(() => {
        if (propIec && propIec !== selectedIEC) {
            setSelectedIEC(propIec);
        }
    }, [propIec]);

    useEffect(() => {
        if (propDates && propDates.length === 2) {
            setDates([dayjs(propDates[0]), dayjs(propDates[1])]);
        }
    }, [propDates]);

    useEffect(() => {
        if (userRole === 'superadmin') {
            getAdminIEC();
        }
        fetchBranchCodeNames();
        fetchData();
        fetchSummary();
    }, [selectedIEC, dates]);

    const handleDateChange = (value) => {
        setDates(value || []);
        if (value && value.length === 2) {
            const from_date = value[0];
            const to_date = value[1];
            if (!from_date || !to_date) { setIsValid(false); return; }
            const oneYearLater = from_date.add(1, 'year');
            if (to_date.isAfter(oneYearLater)) {
                message.error('The selected duration should not exceed 1 year.');
                setIsValid(false);
                return;
            }
            setIsValid(true);
        } else {
            setIsValid(false);
        }
    };

    const handleSubmit = () => {
        if (!selectedIEC) { message.error('Please select an IEC'); return; }
        if (dates.length === 2 && isValid) {
            fetchData();
            fetchSummary();
        } else {
            message.error('Please select a valid date range');
        }
    };

    const getAdminIEC = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await getAllAdminIECsService(token);
            if (response?.data) {
                const options = response.data.map((item, index) => ({
                    value: `${item.iec_no};${item.fullname}`,
                    label: `${item.iec_no} (${item.fullname})`,
                    key: index,
                }));
                setIECOptions(options);
            }
        } catch (err) { message.error('Failed to load IEC list'); }
    };

    const handleIECSelection = (val) => {
        if (val) {
            const [iec_no, iec_name] = val.split(';');
            setSelectedIEC(iec_no);
            setSelectedIECName(iec_name);
        } else {
            setSelectedIEC(null);
            setSelectedIECName('');
        }
    };

    const handleBranchCodeSelection = (selectedValues) => {
        setSelectedBranchCodes(selectedValues);
    };

    // ─── Excel export ─────────────────────────────────────────────────────────
    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();

        // ── Sheet 1: Summary ──────────────────────────────────────────────────
        const summarySheet = workbook.addWorksheet('Summary');

        const tables = [
            {
                title: 'HSN Inconsistency Report',
                headers: ['Business Unit', 'BU Wise Total', '% Consistent', '% Inconsistent', 'Total %'],
                dataKeys: (row) => [
                    reportMode === 'master' ? row.hsn_consistent_pct_wrt_master : row.hsn_consistent_pct,
                    reportMode === 'master' ? row.hsn_inconsistent_pct_wrt_master : row.hsn_inconsistent_pct
                ],
                getTotal: (row) => reportMode === 'master'
                    ? (parseFloat(row.hsn_consistent_wrt_master) || 0) + (parseFloat(row.hsn_inconsistent_wrt_master) || 0)
                    : (parseFloat(row.hsn_consistent) || 0) + (parseFloat(row.hsn_inconsistent) || 0)
            },
            {
                title: 'Duty Rate Inconsistency',
                headers: ['Business Unit', 'BU Wise Total', '% Consistent', '% Inconsistent', 'Total %'],
                dataKeys: (row) => [row.bcd_consistent_pct, row.bcd_inconsistent_pct],
                getTotal: (row) => (parseFloat(row.bcd_consistent) || 0) + (parseFloat(row.bcd_inconsistent) || 0)
            },
            {
                title: 'IGST Consistency',
                headers: ['Business Unit', 'BU Wise Total', '% Consistent', '% Inconsistent', 'Total %'],
                dataKeys: (row) => [row.igst_consistent_pct, row.igst_inconsistent_pct],
                getTotal: (row) => (parseFloat(row.igst_consistent) || 0) + (parseFloat(row.igst_inconsistent) || 0)
            },
            {
                title: 'Duty Rate Accuracy',
                headers: ['Business Unit', 'BU Wise Total', '% Accurate', '% Inaccurate', 'Total %'],
                dataKeys: (row) => [row.bcd_accurate_pct, row.bcd_inaccurate_pct],
                getTotal: (row) => (parseFloat(row.bcd_accurate) || 0) + (parseFloat(row.bcd_inaccurate) || 0)
            },
            {
                title: 'IGST Rate Accuracy',
                headers: ['Business Unit', 'BU Wise Total', '% Accurate', '% Inaccurate', 'Total %'],
                dataKeys: (row) => [row.igst_accurate_pct, row.igst_inaccurate_pct],
                getTotal: (row) => (parseFloat(row.igst_accurate) || 0) + (parseFloat(row.igst_inaccurate) || 0)
            },
            {
                title: 'GTM HSN Adherence/Non Adherence',
                headers: ['Business Unit', 'BU Wise Total', '% Accurate', '% Inaccurate', 'Total %'],
                dataKeys: (row) => [row.hsn_accurate_pct, row.hsn_inaccurate_pct],
                getTotal: (row) => (parseFloat(row.hsn_accurate) || 0) + (parseFloat(row.hsn_inaccurate) || 0)
            }
        ];

        tables.forEach((table) => {
            // Add Title Row
            const titleRow = summarySheet.addRow([table.title]);
            titleRow.font = { bold: true, size: 14 };
            summarySheet.mergeCells(`A${titleRow.number}:E${titleRow.number}`);
            titleRow.eachCell((cell) => {
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9E1F2' } };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });

            // Add Headers
            const headerRow = summarySheet.addRow(table.headers);
            headerRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC000' } };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });

            // Add Data Rows
            summaryData.forEach((row) => {
                const isGrandTotal = row.business_unit === 'Grand Total';
                const cols = table.dataKeys(row);

                const col1 = cols[0] === "-" || cols[0] == null ? "-" : (parseFloat(cols[0]) || 0) / 100;
                const col2 = cols[1] === "-" || cols[1] == null ? "-" : (parseFloat(cols[1]) || 0) / 100;
                const totalCol = cols[0] === "-" || cols[0] == null ? "-" : 1;

                const buTotal = table.getTotal ? table.getTotal(row) : row.total;

                const dataRow = summarySheet.addRow([
                    row.business_unit,
                    buTotal,
                    col1,
                    col2,
                    totalCol
                ]);

                if (isGrandTotal) {
                    dataRow.eachCell((cell) => {
                        cell.font = { bold: true };
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9E1F2' } };
                    });
                }

                dataRow.eachCell((cell) => {
                    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                });
            });

            summarySheet.addRow([]); // Empty row for spacing
        });

        // Auto-fit summary columns and apply percentage format
        summarySheet.columns.forEach((col, index) => {
            col.width = 22;
            if (index >= 2) {
                col.numFmt = '0.00%';
            }
        });

        // ── Chart-data sheet: HSN/BCD consistency by BU ───────────────────────
        // const chartSheet = workbook.addWorksheet('Chart Data');
        // chartSheet.addRow(['Business Unit', '% HSN Consistent', '% HSN Inconsistent', '% BCD Consistent', '% BCD Inconsistent']);
        // chartSheet.getRow(1).eachCell((cell) => {
        //     cell.font = { bold: true };
        //     cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC000' } };
        // });
        // summaryData.forEach((row) => {
        //     chartSheet.addRow([
        //         row.business_unit,
        //         parseFloat(row.hsn_consistent_pct) || 0,
        //         parseFloat(row.hsn_inconsistent_pct) || 0,
        //         parseFloat(row.bcd_consistent_pct) || 0,
        //         parseFloat(row.bcd_inconsistent_pct) || 0,
        //     ]);
        // });
        // chartSheet.columns.forEach((col) => { col.width = 22; });

        // ── Sheet 2: Detail Data ──────────────────────────────────────────────
        const detailSheet = workbook.addWorksheet('Detail Data');
        detailSheet.columns = filteredColumns.map((col) => ({ header: col.header, key: col.dataIndex, width: 20 }));
        detailSheet.getRow(1).eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC000' } };
            cell.alignment = { vertical: 'middle' };
        });
        jsonData.forEach((obj) => {
            detailSheet.addRow({
                ...obj,
                be_date: obj.be_date ? dayjs(obj.be_date).format('DD/MM/YYYY') : '',
                master_bcd_equal: obj.master_bcd_equal === true || obj.master_bcd_equal === 'true' ? 'Yes' : 'No',
                master_igst_equal: obj.master_igst_equal === true || obj.master_igst_equal === 'true' ? 'Yes' : 'No',
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Inconsistency Report - ${selectedIECName} as on ${getTodaysDate()}.xlsx`);
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <Fragment>
            <style>{tableStyles}</style>
            <Row>
                <Col sm="12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between">
                            <div className="header-title">
                                <h4 className="card-title">
                                    Inconsistency Report as on {formattedDate} of {selectedIECName}
                                </h4>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <ColumnSelector
                                    allColumns={columnNames}
                                    visibleKeys={visibleKeys}
                                    setVisibleKeys={setVisibleKeys}
                                    onReset={resetToDefault}
                                />
                                <button className="btn btn-primary cip-btn-submit" onClick={exportToExcel}>
                                    Export to Excel
                                </button>
                            </div>
                        </Card.Header>

                        {/* ── Filter Bar ── */}
                        <Card.Body className="d-flex justify-content-start align-items-center gap-2 ps-4">
                            {userRole === 'superadmin' && (
                                <Select
                                    showSearch
                                    placeholder="Select IEC"
                                    onChange={handleIECSelection}
                                    allowClear
                                    options={IECOptions}
                                    style={{ width: '300px' }}
                                />
                            )}
                            <ReportDateFilter dates={dates} onChange={handleDateChange} />
                            <Select
                                mode="multiple"
                                showSearch
                                placeholder="Select Branch"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                onChange={handleBranchCodeSelection}
                                allowClear
                                options={branchCodeNames}
                                style={{ width: '300px' }}
                            />
                            <Button type="primary" className="cip-btn-submit" onClick={handleSubmit}>
                                Submit
                            </Button>
                        </Card.Body>

                        <Card.Body className="d-flex justify-content-start align-items-center gap-2 ps-4 pt-0 pb-2">
                            <Radio.Group
                                value={reportMode}
                                onChange={(e) => setReportMode(e.target.value)}
                            >
                                <Radio value="data">wrt. data</Radio>
                                <Radio value="master">wrt. master</Radio>
                            </Radio.Group>
                        </Card.Body>

                        {/* ── Summary Table ── */}
                        <Card.Body className="px-0 report-page">
                            <div style={{ padding: '0 16px 8px', fontWeight: 600, fontSize: 14, color: '#1a1a2e' }}>
                                📊 Business Unit-wise HSN &amp; BCD Summary
                            </div>
                            <Table
                                className="inconsistency-summary-table"
                                columns={summaryColumns}
                                dataSource={summaryData}
                                loading={isSummaryLoading}
                                pagination={false}
                                rowKey={(r, i) => r.business_unit || i}
                                scroll={{ x: 'max-content' }}
                                
                                size="small"
                                bordered
                                rowClassName={(record) =>
                                    record.business_unit === 'Grand Total' ? 'ant-table-summary-row' : ''
                                }
                                style={{ marginBottom: 0 }}
                            />

                            <hr />

                            {/* ── Detail Table ── */}
                            <div style={{ padding: '0 16px 8px', fontWeight: 600, fontSize: 14, color: '#1a1a2e' }}>
                                📋 Detail Data
                            </div>
                            <Table
                                className="inconsistency-table"
                                bordered
                                columns={filteredColumns}
                                dataSource={jsonData}
                                loading={isLoading}
                                rowKey={(record, index) => record.duties_id || index}
                                scroll={{ x: 'max-content' }}
                                
                                pagination={{ showTotal: (total) => `Total ${total} records`, showSizeChanger: true }} />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Fragment>
    );
};

export default InconsistencyReport;
